import { LanguageService } from '@app/core/services/language.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormlyModule } from "@ngx-formly/core";
import { IonButton, IonSpinner, IonInput } from '@ionic/angular/standalone';
import { Constants } from '@app/core/services/constants';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";
import { SubSink } from 'subsink';

declare var google: any;

export interface LocationData {
  address: string;
  street: string;
  zipCode: string;
  city: string;
  country:string;
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'app-formly-field-location',
  templateUrl: './formly-field-location.component.html',
  styleUrls: ['./formly-field-location.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    FormlyModule,
    IonButton,
    IonSpinner,
    IonInput,
    DynamicIconComponent
],
})
export class FormlyFieldLocationComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  private static mapsApiLoaded = false;
  private static mapsApiLoadingPromise: Promise<void> | null = null;
  private static authFailureHandlerRegistered = false;
  @ViewChild('mapContainer', { static: false }) mapContainer?: ElementRef;
  @ViewChild('addressInput', { read: ElementRef, static: false }) addressInput?: ElementRef;

  public isLoading = false;
  public locationData: LocationData | null = null;
  public errorMessage: string | null = null;
  public mapInitialized = false;
  public manualAddress = '';
  
  private map: any;
  private marker: any;
  private geocoder: any;
  private autocomplete: any;
  private autocompleteElement?: any;
  private lastGeocodedValue = '';
  private shadowInputListenerAttached = false;
  private valuePollTimer: ReturnType<typeof setInterval> | null = null;
  private valuePollStart: number | null = null;
  private valuePollLastKick: number | null = null;
  private placePollTimer: ReturnType<typeof setInterval> | null = null;
  private placePollStart: number | null = null;
  private accuracyCircle: any;
  private lastAccuracy: number | null = null;
  private defaultZoomLevel = 17;
  private subsink = new SubSink();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private languageService: LanguageService,
    private ngZone: NgZone
  ) {
    super();
  }

  ngOnInit(): void {
    // Load existing location data if available

        // Subscribe to formControl value changes to update localValue
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });
    
    this.updateLocalValue(this.formControl.value);

    // Load Google Maps API
    this.loadGoogleMapsAPI();

  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  private updateLocalValue(value: any): void {
    if (value) {
      try {
        this.locationData = typeof value === 'string' 
          ? JSON.parse(value) 
          : value;
        
        // Initialize manual address field with existing address
        if (this.locationData && this.locationData.address) {
          this.manualAddress = this.locationData.address;
          this.setAutocompleteInputValue(this.manualAddress);
        }
        if(value.latitude && value.longitude) {
          if (this.map) {
            this.map.setCenter({ lat: value.latitude, lng: value.longitude });
            this.map.setZoom(this.defaultZoomLevel);
            this.addMarker(value.latitude, value.longitude);
            if (this.lastAccuracy !== null) {
              this.addAccuracyCircle(value.latitude, value.longitude, this.lastAccuracy);
            } else {
              this.removeAccuracyCircle();
            }
          }
        }
      } catch (error) {
        console.error('Error parsing location data:', error);
      }
    }
  }

  /**
   * Load Google Maps API script
   */
  private loadGoogleMapsAPI(): void {
    if (FormlyFieldLocationComponent.mapsApiLoaded || (typeof google !== 'undefined' && google.maps)) {
      FormlyFieldLocationComponent.mapsApiLoaded = true;
      this.ensureGeocoder();
      if (this.shouldShowMap()) {
        this.initializeMap();
      } else {
        this.initializeAutocomplete();
      }
      return;
    }

    if (!FormlyFieldLocationComponent.mapsApiLoadingPromise) {
      const platform = 'browser';
      console.warn('TO DO TODO: detect platform for Google Maps API key selection');
      const apiKey = Constants.googleMapsKeys[platform];
      if (!apiKey) {
        this.errorMessage = 'Google Maps API key not configured';
        this.changeDetectorRef.markForCheck();
        return;
      }

      if (!FormlyFieldLocationComponent.authFailureHandlerRegistered) {
        (window as any).gm_authFailure = () => {
          this.ngZone.run(() => {
            this.errorMessage = 'Google Maps API authentication failed. Please check the API key and ensure the following APIs are enabled in Google Cloud Console: Maps JavaScript API, Places API.';
            console.error('Google Maps authentication failed');
            this.changeDetectorRef.markForCheck();
          });
        };
        FormlyFieldLocationComponent.authFailureHandlerRegistered = true;
      }

      FormlyFieldLocationComponent.mapsApiLoadingPromise = new Promise<void>((resolve, reject) => {
        const callbackName = 'initGoogleMapsCallback_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
        (window as any)[callbackName] = () => {
          FormlyFieldLocationComponent.mapsApiLoaded = true;
          delete (window as any)[callbackName];
          resolve();
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&v=weekly&loading=async&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          delete (window as any)[callbackName];
          reject(new Error('Failed to load Google Maps API script'));
        };
        document.head.appendChild(script);
      });
    }

    FormlyFieldLocationComponent.mapsApiLoadingPromise
      .then(() => {
        this.ngZone.run(() => {
          this.ensureGeocoder();
          if (this.shouldShowMap()) {
            this.initializeMap();
          } else {
            this.initializeAutocomplete();
          }
        });
      })
      .catch((error: Error) => {
        this.ngZone.run(() => {
          console.error(error);
          this.errorMessage = 'Failed to load Google Maps API script. Please check your internet connection.';
          this.changeDetectorRef.markForCheck();
        });
      });
  }

  /**
   * Initialize the Google Map
   */
  private initializeMap(): void {
    if (!this.shouldShowMap()) {
      return;
    }
    if (this.mapInitialized) {
      return;
    }
    
    if (!this.mapContainer) {
      // Map container not ready yet, try again after a short delay
      setTimeout(() => {
        if (this.mapContainer && !this.mapInitialized) {
          this.initializeMap();
        }
      }, 100);
      return;
    }

    this.geocoder = new google.maps.Geocoder();
    
    const defaultCenter = this.locationData?.latitude !== null && this.locationData?.latitude !== undefined && this.locationData?.longitude !== null && this.locationData?.longitude !== undefined
      ? { lat: this.locationData.latitude, lng: this.locationData.longitude }
      : { lat: 48.8566, lng: 2.3522 }; // Default to Paris

    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: defaultCenter,
      zoom: this.locationData?.latitude !== null && this.locationData?.latitude !== undefined && this.locationData?.longitude !== null && this.locationData?.longitude !== undefined ? this.defaultZoomLevel : 4,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      mapId: 'map_' + Math.random().toString(36).substring(2, 11), // Unique mapId per instance
    });

    // Add marker if location exists
    if (this.locationData?.latitude !== null && this.locationData?.latitude !== undefined && this.locationData?.longitude !== null && this.locationData?.longitude !== undefined) {
      this.addMarker(this.locationData.latitude, this.locationData.longitude);
    }

    // Allow clicking on map to set location
    this.map.addListener('click', (event: any) => {
      this.ngZone.run(() => {
        this.updateLocation(event.latLng.lat(), event.latLng.lng());
      });
    });

    this.mapInitialized = true;
    this.changeDetectorRef.markForCheck();
    this.initializeAutocomplete();
  }

  /**
   * Initialize Google Places Autocomplete using PlaceAutocompleteElement
   */
  private initializeAutocomplete(): void {
    if (!this.addressInput) {
      // Retry after a short delay if input not ready
      setTimeout(() => this.initializeAutocomplete(), 100);
      return;
    }

    if (this.autocompleteElement) {
      return;
    }

    // Get the native input element from ion-input
    const ionInput = this.addressInput.nativeElement;
    
    // Wait for ion-input to be ready and get its native input
    ionInput.getInputElement().then(async (inputElement: HTMLInputElement) => {
      if (!inputElement) {
        console.error('No input element found');
        return;
      }

      try {
        let PlaceAutocompleteElementCtor = google.maps?.places?.PlaceAutocompleteElement;
        if (!PlaceAutocompleteElementCtor && typeof google.maps?.importLibrary === 'function') {
          const placesLib = await google.maps.importLibrary('places');
          PlaceAutocompleteElementCtor = (placesLib as any).PlaceAutocompleteElement;
        }

        if (!PlaceAutocompleteElementCtor) {
          console.error('[LocationField] PlaceAutocompleteElement is not available');
          this.errorMessage = 'Places Autocomplete is not available. Please use manual address entry.';
          this.changeDetectorRef.markForCheck();
          return;
        }

        const autocompleteElement = new PlaceAutocompleteElementCtor();
        autocompleteElement.style.display = 'block';
        autocompleteElement.style.width = '100%';
        autocompleteElement.id = 'place-autocomplete-input-' + Math.random().toString(36).substring(2);
        this.autocompleteElement = autocompleteElement;

        // Hide the ion-input and insert the web component in its place
        const hostElement = this.addressInput!.nativeElement;
        hostElement.style.display = 'none';

        hostElement.parentElement.insertBefore(autocompleteElement, hostElement);
        this.setupAutocompleteInputListeners(autocompleteElement);
        this.setAutocompleteInputValue(this.manualAddress);

        const handlePlaceEvent = async (event: any) => {
          const place = event?.place || event?.detail?.place || event?.detail || this.autocompleteElement?.place;
          if (!place) {
            const value = this.getAutocompleteInputValue();
            if (this.shouldGeocodeAutocompleteValue(value) && this.lastGeocodedValue !== value) {
              this.manualAddress = value;
              this.lastGeocodedValue = value;
              this.geocodeAddress();
            }
            return;
          }

          try {
            if (typeof place.fetchFields === 'function') {
              await place.fetchFields({ fields: ['location', 'formattedAddress', 'addressComponents'] });
            }

            this.ngZone.run(() => {
              const location = place.location || place.geometry?.location;
              if (!location) {
                this.errorMessage = 'No details available for selected address';
                this.changeDetectorRef.markForCheck();
                return;
              }

              const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
              const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

              this.applyPlaceSelection(place, lat, lng);

              this.errorMessage = null;
              this.changeDetectorRef.markForCheck();
            });
          } catch (error: any) {
            console.error('Error fetching place fields:', error);
            this.ngZone.run(() => {
              this.errorMessage = 'Failed to retrieve address details. Please try again.';
              this.changeDetectorRef.markForCheck();
            });
          }
        };

        autocompleteElement.addEventListener('gmp-placeselect', handlePlaceEvent as EventListener);
        autocompleteElement.addEventListener('gmp-placechange', handlePlaceEvent as EventListener);

        // Capture events at the document level in case they do not bubble from the web component
        document.addEventListener('gmp-placeselect', handlePlaceEvent as EventListener, true);
        document.addEventListener('gmp-placechange', handlePlaceEvent as EventListener, true);
        const tryHandleFromElement = (event: Event) => {
          this.tryHandlePlaceFromElement(event.type);
        };
        autocompleteElement.addEventListener('input', tryHandleFromElement as EventListener);
        autocompleteElement.addEventListener('change', tryHandleFromElement as EventListener);
        autocompleteElement.addEventListener('blur', tryHandleFromElement as EventListener);
      } catch (error: any) {
        console.error('Error initializing autocomplete:', error);
        this.errorMessage = 'Failed to initialize address autocomplete. Please use manual address entry.';
        this.changeDetectorRef.markForCheck();
      }
    }).catch((error: any) => {
      console.error('Error getting input element from ion-input:', error);
    });
  }

  /**
   * Add or update accuracy circle on the map
   */
  private addAccuracyCircle(lat: number, lng: number, accuracy: number): void {
    // Remove existing accuracy circle
    this.removeAccuracyCircle();

    // Create new accuracy circle
    this.accuracyCircle = new google.maps.Circle({
      strokeColor: '#4285F4',
      strokeOpacity: 0.4,
      strokeWeight: 1,
      fillColor: '#4285F4',
      fillOpacity: 0.15,
      map: this.map,
      center: { lat, lng },
      radius: accuracy, // accuracy is in meters
    });
  }

  /**
   * Remove accuracy circle from the map
   */
  private removeAccuracyCircle(): void {
    if (this.accuracyCircle) {
      this.accuracyCircle.setMap(null);
      this.accuracyCircle = null;
    }
  }

  /**
   * Add or update marker on the map using AdvancedMarkerElement
   */
  private addMarker(lat: number, lng: number): void {
    if (this.marker) {
      this.marker.map = null;
    }

    const position = { lat, lng };
    
    // Create a pin element (teardrop style)
    const pinElement = new google.maps.marker.PinElement({
      background: '#EA4335',
      borderColor: '#FFFFFF',
      glyphColor: '#FFFFFF',
      scale: 1.2,
    });

    this.marker = new google.maps.marker.AdvancedMarkerElement({
      position: position,
      map: this.map,
      content: pinElement as any,
      gmpDraggable: !this.isDisabled(),
    });

    // Allow dragging marker to change location
    if (!this.isDisabled()) {
      this.marker.addListener('dragend', (event: any) => {
        this.ngZone.run(() => {
          const newPosition = this.marker.position;
          this.updateLocation(newPosition.lat, newPosition.lng);
        });
      });
    }
  }

  /**
   * Get current geolocation
   */
  public getCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.errorMessage = 'Geolocation is not supported by this browser';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.changeDetectorRef.markForCheck();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.ngZone.run(() => {
          this.updateLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy);
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        });
      },
      (error) => {
        this.ngZone.run(() => {
          this.isLoading = false;
          this.errorMessage = `Geolocation error: ${error.message}`;
          this.changeDetectorRef.markForCheck();
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  /**
   * Update location with coordinates and reverse geocode to get address
   */
  private updateLocation(lat: number, lng: number, placeOrAccuracy?: any): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.changeDetectorRef.markForCheck();

    let accuracy: number | undefined;
    if (typeof placeOrAccuracy === 'number') {
      accuracy = placeOrAccuracy;
    } else if (placeOrAccuracy && placeOrAccuracy.geometry && placeOrAccuracy.geometry.accuracy) {
      accuracy = placeOrAccuracy.geometry.accuracy;
    }
    this.lastAccuracy = accuracy ?? null;

    // Update map center and marker
    if (this.map) {
      this.map.setCenter({ lat, lng });
      this.map.setZoom(this.defaultZoomLevel);
      this.addMarker(lat, lng);
      
      // Add accuracy circle if accuracy is provided
      if (accuracy !== undefined) {
        this.addAccuracyCircle(lat, lng, accuracy);
      } else {
        // Remove accuracy circle if not from GPS
        this.removeAccuracyCircle();
      }
    }

    if (typeof placeOrAccuracy === 'object' && placeOrAccuracy.formattedAddress && placeOrAccuracy.addressComponents) {
      this.locationData = this.getLocationDataFromPlace(placeOrAccuracy, lat, lng);
      this.manualAddress = this.locationData.address;

      this.formControl.setValue(this.locationData);
      this.onChange(this.locationData);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();

      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }

    // Reverse geocode to get address
    this.geocoder.geocode(
      { location: { lat, lng } },
      (results: any[], status: string) => {
        this.ngZone.run(() => {
          if (status === 'OK' && results[0]) {
            // this.locationData = {
            //   address: results[0].formatted_address,
            //   latitude: lat,
            //   longitude: lng,
            // };

            this.locationData = this.getLocationDataFromReverseGeocoderResult(results, lat, lng);

            // Update manual address input field
            this.manualAddress = this.locationData.address;

            // Update form control
            this.formControl.setValue(this.locationData);
            this.onChange(this.locationData);
            this.formControl.markAsDirty();
            this.formControl.markAsTouched();
          } else {
            this.errorMessage = 'Failed to get address for this location';
          }
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        });
      }
    );
  }

  private applyPlaceSelection(place: any, lat: number, lng: number): void {
    const hasComponents = Array.isArray(place.addressComponents) && place.addressComponents.length > 0;
    const formattedAddress = place.formattedAddress || place.formatted_address || '';

    // Update map immediately
    if (this.map) {
      this.map.setCenter({ lat, lng });
      this.map.setZoom(this.defaultZoomLevel);
      this.addMarker(lat, lng);
      this.removeAccuracyCircle();
    }

    if (hasComponents && formattedAddress) {
      this.locationData = this.getLocationDataFromPlace(place, lat, lng);
    } else {
      this.locationData = {
        address: formattedAddress,
        street: '',
        zipCode: '',
        city: '',
        country: '',
        latitude: lat,
        longitude: lng,
      };
    }

    if (this.locationData.address) {
      this.manualAddress = this.locationData.address;
    }

    this.formControl.setValue(this.locationData);
    this.onChange(this.locationData);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();

    this.isLoading = false;
    this.errorMessage = null;
    this.changeDetectorRef.markForCheck();

    // Fill missing fields via reverse geocoding if components were not provided
    if (!hasComponents) {
      this.updateLocation(lat, lng);
    }
  }

  private async tryHandlePlaceFromElement(reason: string): Promise<void> {
    const place = this.autocompleteElement?.place;
    const value = this.getAutocompleteInputValue();
    if (!place) {
      if (value && value !== this.manualAddress) {
        this.manualAddress = value;
      }

      if (reason === 'input' || reason === 'change') {
        this.pollPlaceFromElement();
        this.pollAutocompleteValue();
      }

      if (reason === 'blur' && this.shouldGeocodeAutocompleteValue(value) && this.lastGeocodedValue !== value) {
        this.lastGeocodedValue = value;
        this.geocodeAddress();
      }
      return;
    }

    try {
      if (typeof place.fetchFields === 'function') {
        await place.fetchFields({ fields: ['location', 'formattedAddress', 'addressComponents'] });
      }

      const location = place.location || place.geometry?.location;
      if (!location) return;

      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

      this.ngZone.run(() => {
        this.applyPlaceSelection(place, lat, lng);
      });
    } catch (error: any) {
      console.error('[LocationField] failed to handle place from element', error);
    }
  }

  private pollPlaceFromElement(): void {
    if (this.placePollTimer) {
      return;
    }

    this.placePollStart = Date.now();
    this.placePollTimer = setInterval(async () => {
      const place = this.autocompleteElement?.place;
      if (place) {
        if (this.placePollTimer) {
          clearInterval(this.placePollTimer);
          this.placePollTimer = null;
          this.placePollStart = null;
        }
        await this.tryHandlePlaceFromElement('poll');
        return;
      }

      if (this.placePollStart && Date.now() - this.placePollStart > 1500) {
        clearInterval(this.placePollTimer!);
        this.placePollTimer = null;
        this.placePollStart = null;
      }
    }, 100);
  }

  private pollAutocompleteValue(): void {
    const now = Date.now();
    this.valuePollLastKick = now;

    if (!this.valuePollTimer) {
      this.valuePollStart = now;
      this.valuePollTimer = setInterval(() => {
      const value = this.getAutocompleteInputValue();
      if (this.shouldGeocodeAutocompleteValue(value) && this.lastGeocodedValue !== value) {
        this.manualAddress = value;
        this.lastGeocodedValue = value;
        this.geocodeAddress();
        clearInterval(this.valuePollTimer!);
        this.valuePollTimer = null;
        this.valuePollStart = null;
        this.valuePollLastKick = null;
        return;
      }

      const nowTick = Date.now();
      const timeSinceStart = this.valuePollStart ? nowTick - this.valuePollStart : 0;
      const timeSinceKick = this.valuePollLastKick ? nowTick - this.valuePollLastKick : 0;

      if (timeSinceStart > 6000 || timeSinceKick > 2500) {
        clearInterval(this.valuePollTimer!);
        this.valuePollTimer = null;
        this.valuePollStart = null;
        this.valuePollLastKick = null;
      }
    }, 100);
    }
  }

  private setupAutocompleteInputListeners(element: any, attempt = 0): void {
    if (this.shadowInputListenerAttached) {
      return;
    }

    const input = element?.shadowRoot?.querySelector('input') as HTMLInputElement | null;
    if (input) {
      this.shadowInputListenerAttached = true;
      input.addEventListener('input', () => this.tryHandlePlaceFromElement('shadow-input'));
      input.addEventListener('change', () => this.tryHandlePlaceFromElement('shadow-change'));
      input.addEventListener('blur', () => this.tryHandlePlaceFromElement('shadow-blur'));
      return;
    }

    if (attempt < 10) {
      setTimeout(() => this.setupAutocompleteInputListeners(element, attempt + 1), 100);
    }
  }

  private setAutocompleteInputValue(value: string): void {
    if (!value || !this.autocompleteElement) {
      return;
    }

    const element = this.autocompleteElement as any;
    const shadowInput = element?.shadowRoot?.querySelector('input') as HTMLInputElement | null;

    if (shadowInput && shadowInput.value !== value) {
      shadowInput.value = value;
    }

    if (typeof element.value === 'string' && element.value !== value) {
      element.value = value;
    } else if (typeof element.inputValue === 'string' && element.inputValue !== value) {
      element.inputValue = value;
    }
  }

  private getAutocompleteInputValue(): string {
    const element = this.autocompleteElement as any;
    const shadowInput = element?.shadowRoot?.querySelector('input') as HTMLInputElement | null;
    return (shadowInput?.value || element?.value || element?.inputValue || '').toString();
  }

  private shouldGeocodeAutocompleteValue(value: string): boolean {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;

    // Heuristic: full addresses typically include a comma and are longer than a few chars.
    return trimmed.length >= 8 && trimmed.includes(',');
  }

  private getLocationDataFromPlace(place: any, lat: number, lng: number): LocationData {
    const getComponent = (type: string) => {
      const comp = place.addressComponents?.find((c: any) => c.types.includes(type));
      return comp ? (comp.longText || comp.long_name || '') : '';
    };

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const city = getComponent('locality');
    const postalCode = getComponent('postal_code');
    const country = getComponent('country');

    return {
      address: place.formattedAddress,
      street: [streetNumber, route].filter(part => part).join(' '),
      zipCode: postalCode,
      city: city,
      country: country,
      latitude: lat,
      longitude: lng,
    };
  }

  private getLocationDataFromReverseGeocoderResult(results: any, lat: number, lng: number): LocationData {
    const result = results.find((res: any) => res?.types?.includes('street_address')) || results[0];
    const streetNumber = result.address_components.find((comp: any) => comp.types.includes('street_number'))?.long_name || '';
    const route = result.address_components.find((comp: any) => comp.types.includes('route'))?.long_name || '';
    const city = result.address_components.find((comp: any) => comp.types.includes('locality'))?.long_name || '';
    const postalCode = result.address_components.find((comp: any) => comp.types.includes('postal_code'))?.long_name || '';
    const country = result.address_components.find((comp: any) => comp.types.includes('country'))?.long_name || '';
    const locationData: LocationData = {
      address: result.formatted_address,
      street: [streetNumber, route].filter(part => part).join(' '),
      zipCode: postalCode,
      city: city,
      country: country,
      latitude: lat,
      longitude: lng,
    };
    return locationData;
  }

  /**
   * Geocode address and update location
   */
  public geocodeAddress(): void {
    if (!this.manualAddress || !this.manualAddress.trim()) {
      this.errorMessage = 'Please enter an address';
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.changeDetectorRef.markForCheck();

    this.geocoder.geocode(
      { address: this.manualAddress },
      (results: any[], status: string) => {
        this.ngZone.run(() => {
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();

            // Update map center and marker
            if (this.map) {
              this.map.setCenter({ lat, lng });
              this.map.setZoom(this.defaultZoomLevel);
              this.addMarker(lat, lng);
            }

            // Update location data
            // this.locationData = {
            //   address: results[0].formatted_address,
            //   latitude: lat,
            //   longitude: lng,
            // };
            this.locationData = this.getLocationDataFromReverseGeocoderResult(results, lat, lng);


            // Update form control
            this.formControl.setValue(this.locationData);
            this.onChange(this.locationData);
            this.formControl.markAsDirty();
            this.formControl.markAsTouched();
          } else {
            this.errorMessage = 'Address not found. Please try a different address.';
          }
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        });
      }
    );
  }

  /**
   * Clear the location data
   */
  public clearLocation(): void {
    this.locationData = null;
    this.manualAddress = '';
    this.errorMessage = null;
    this.lastAccuracy = null;
    this.formControl.setValue(null);
    this.onChange(this.locationData);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    
    if (this.marker) {
      this.marker.map = null;
      this.marker = null;
    }
    
    // Remove accuracy circle
    this.removeAccuracyCircle();
    
    if (this.map) {
      this.map.setCenter({ lat: 48.8566, lng: 2.3522 });
      this.map.setZoom(10);
    }
    
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Check if field is disabled
   */
  public isDisabled(): boolean {
    return this.options.formState.disabled || this.field.props?.['disabled'] || this.field.props?.['readonly'] || false;
  }

  public shouldShowMap(): boolean {
    return this.field.props?.['showMap'] === true;
  }

  private ensureGeocoder(): void {
    if (!this.geocoder && typeof google !== 'undefined' && google.maps) {
      this.geocoder = new google.maps.Geocoder();
    }
  }

  /**
   * Format coordinates for display
   */
  public getFormattedCoordinates(): string {
    if (!this.locationData) return '';
    if (this.locationData?.latitude !== null && this.locationData?.latitude !== undefined && this.locationData?.longitude !== null && this.locationData?.longitude !== undefined) {
      return `(${this.locationData.latitude.toFixed(6)}, ${this.locationData.longitude.toFixed(6)})`;
    } else {
      return this.languageService.instant('LOCATION.UNAVAILABLE_COORDINATES');
    }
  }
}
